import { useState } from 'react';

interface ProductUploadModalProps {
  supplier: { name: string; city: string; contact: string };
  onClose: () => void;
  onSuccess: () => void;
}

export default function ProductUploadModal({ supplier, onClose, onSuccess }: ProductUploadModalProps) {
  const [form, setForm] = useState({
    productName: '',
    nameEn: '',
    category: '',
    description: '',
    price: '',
    originalPrice: '',
    currency: '¥',
    stock: '',
    shippingDays: '1-2天',
    wechat: '',
    email: supplier.contact.includes('@') ? supplier.contact : '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const categories = [
    { key: 'clothing', label: '女装' },
    { key: 'pants', label: '裤装' },
    { key: 'infant', label: '婴儿服' },
    { key: 'hat', label: '帽子' },
    { key: 'book', label: '书籍' },
    { key: 'stationery', label: '文具' },
    { key: 'other', label: '其他' },
  ];

  const currencies = ['¥', 'HK$', 'S$', 'A$', 'AED', '£', '$', '€'];
  const shippingOptions = ['当日达', '1-2天', '2-3天', '3-5天', '5-7天', '7-14天'];

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.productName.trim()) errs.productName = '请输入商品名称';
    if (!form.category) errs.category = '请选择商品分类';
    if (!form.description.trim()) errs.description = '请输入商品描述';
    if (!form.price || isNaN(Number(form.price))) errs.price = '请输入有效价格';
    if (!form.stock || isNaN(Number(form.stock))) errs.stock = '请输入库存数量';
    if (form.description.length > 500) errs.description = '描述不能超过500字';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      onSuccess();
    }, 1500);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => setImagePreview(ev.target?.result as string);
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4 py-8 overflow-y-auto">
      <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl my-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-rose-600 px-8 py-5 rounded-t-3xl relative">
          <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 cursor-pointer">
            <i className="ri-close-line"></i>
          </button>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-white/20 rounded-full">
              <i className="ri-upload-cloud-line text-white"></i>
            </div>
            <div>
              <h2 className="text-white font-bold">上传商品</h2>
              <p className="text-white/70 text-xs">{supplier.name} · {supplier.city}仓</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="px-8 py-6 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Image Upload */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">商品图片</label>
            <label className="flex flex-col items-center justify-center w-full h-36 border-2 border-dashed border-gray-200 rounded-2xl cursor-pointer hover:border-rose-300 hover:bg-rose-50/30 transition-all overflow-hidden">
              {imagePreview ? (
                <img src={imagePreview} alt="preview" className="w-full h-full object-cover" />
              ) : (
                <div className="flex flex-col items-center gap-2 text-gray-400">
                  <i className="ri-image-add-line text-3xl"></i>
                  <span className="text-xs">点击上传商品图片</span>
                  <span className="text-xs text-gray-300">支持 JPG、PNG，建议尺寸 800×800</span>
                </div>
              )}
              <input type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
            </label>
          </div>

          {/* Product Name */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">商品名称 *</label>
              <input
                type="text"
                placeholder="中文商品名"
                value={form.productName}
                onChange={(e) => setForm({ ...form, productName: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-rose-400 transition-colors"
              />
              {errors.productName && <p className="text-xs text-red-500 mt-1">{errors.productName}</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">英文名称（可选）</label>
              <input
                type="text"
                placeholder="English name"
                value={form.nameEn}
                onChange={(e) => setForm({ ...form, nameEn: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-rose-400 transition-colors"
              />
            </div>
          </div>

          {/* Category */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">商品分类 *</label>
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat.key}
                  type="button"
                  onClick={() => setForm({ ...form, category: cat.key })}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${form.category === cat.key ? 'bg-rose-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
            {errors.category && <p className="text-xs text-red-500 mt-1">{errors.category}</p>}
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">商品描述 * <span className="text-gray-300">（最多500字）</span></label>
            <textarea
              placeholder="详细描述商品材质、规格、特点等..."
              value={form.description}
              onChange={(e) => {
                if (e.target.value.length <= 500) setForm({ ...form, description: e.target.value });
              }}
              rows={3}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-rose-400 transition-colors resize-none"
            />
            <div className="flex justify-between">
              {errors.description && <p className="text-xs text-red-500">{errors.description}</p>}
              <p className="text-xs text-gray-300 ml-auto">{form.description.length}/500</p>
            </div>
          </div>

          {/* Price */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">货币</label>
              <select
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-rose-400 bg-white cursor-pointer"
              >
                {currencies.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">售价 *</label>
              <input
                type="number"
                placeholder="0.00"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-rose-400 transition-colors"
              />
              {errors.price && <p className="text-xs text-red-500 mt-1">{errors.price}</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">原价（可选）</label>
              <input
                type="number"
                placeholder="0.00"
                value={form.originalPrice}
                onChange={(e) => setForm({ ...form, originalPrice: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-rose-400 transition-colors"
              />
            </div>
          </div>

          {/* Stock + Shipping */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">库存数量 *</label>
              <input
                type="number"
                placeholder="库存件数"
                value={form.stock}
                onChange={(e) => setForm({ ...form, stock: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-rose-400 transition-colors"
              />
              {errors.stock && <p className="text-xs text-red-500 mt-1">{errors.stock}</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">发货时效</label>
              <select
                value={form.shippingDays}
                onChange={(e) => setForm({ ...form, shippingDays: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-rose-400 bg-white cursor-pointer"
              >
                {shippingOptions.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {/* Contact Info */}
          <div className="bg-amber-50 rounded-2xl p-4 space-y-3">
            <p className="text-xs font-semibold text-amber-700 flex items-center gap-1.5">
              <i className="ri-contacts-line"></i> 买家联系方式（买家下单后可见）
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">微信号</label>
                <input
                  type="text"
                  placeholder="您的微信号"
                  value={form.wechat}
                  onChange={(e) => setForm({ ...form, wechat: e.target.value })}
                  className="w-full border border-amber-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-amber-400 bg-white transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">邮箱</label>
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full border border-amber-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-amber-400 bg-white transition-colors"
                />
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 bg-gradient-to-r from-amber-500 to-rose-600 text-white rounded-xl font-semibold text-sm hover:opacity-90 transition-all cursor-pointer whitespace-nowrap disabled:opacity-60"
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin"></i> 上传中...
              </span>
            ) : (
              <span><i className="ri-upload-cloud-line mr-2"></i>发布商品</span>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
